import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Helper } from "./helper";
import { Logger } from "./logger";
import VastParser, { AdObj, Creative, CreativeData } from "./vast-parser";
import { PauseAdsInfo } from "./app-api-config";

const DESIGN_FRAME = { width: 1920, height: 1080 };
const PAUSE_AD_Z_INDEX = 900;
const CONTAINER_ID = 'pause-ads-container';
const FADE_MS = 300;
const SHOW_DELAY_MS = 500;

export class PauseAds {
    config: PauseAdsInfo | null = null;
    adObj: AdObj | null = null;
    videoPlayer: any = null;
    shouldDisplayAd = false;
    adDisplayedStatus = false;
    currentAdIndex = 0;
    currentAdDuration = 0;
    adCycleCount = 0;
    showTimerId: any = null;
    dismissTimerId: any = null;
    trackerTimerIds: any[] = [];
    trackersStatus = {
        start: false,
        midpoint: false,
        complete: false
    };

    constructor(videoPlayer: any) {
        const { appState } = AppData as AppSetup;
        this.config = appState?.getAppApiConfig()?.adsdkConfig?.pauseAds || null;
        this.videoPlayer = videoPlayer;
        if (this.init()) {
            this.loadCreatives();
        }
    }

    /**
     * Whether pause ads run at all. There is no pause-specific flag on the index
     * API, so the format rides the API's `bugadenabled` - the two overlay formats
     * are switched on together per site - and is additionally gated by the
     * app-level config param `enablePauseAds`, which decides whether this
     * build/platform offers the format in the first place.
     */
    static isEnabled = (): boolean => {
        const { appConfig, appState } = AppData as AppSetup;
        if (!appConfig?.enablePauseAds) {
            return false;
        }
        const bugAds = appState?.getAppApiConfig()?.adsdkConfig?.bugAds;
        return Helper.toBoolean(bugAds?.bugadenabled, false);
    }

    init = () => {
        Log.info('adlog', 'PauseAds.init() called', '');
        if (!this.config || $.isEmptyObject(this.config)) {
            return false;
        }
        if (!PauseAds.isEnabled()) {
            Log.info('adlog', 'PauseAds.init() - pause ads disabled', '');
            return false;
        }
        this.shouldDisplayAd = true;
        return true;
    }

    /**
     * Pull the pre-fetched creatives from the app-level cache instead of hitting
     * the ad server per video. If the launch prefetch hasn't populated the cache
     * yet we run without creatives for now and pick them up on a later pause
     * (getCreative retries); if it never ran or errored, kick an idempotent fetch.
     */
    loadCreatives = () => {
        const cache = this.getCache();
        if (cache?.status === 'ready' && cache.adObj) {
            this.adObj = cache.adObj;
        } else {
            this.adObj = null;
            if (cache && (cache.status === 'idle' || cache.status === 'error')) {
                VastParser.prefetch('pause');
            }
        }
    }

    /**
     * The viewer paused: arm the appearance timer rather than opening the banner
     * straight away. The delay keeps an ad off the screen on a pause the viewer
     * undoes immediately - a mis-pressed PAUSE, or a pause the player passes
     * through on its way into trickplay - and gives the video screen's own state
     * changes time to cancel it first.
     *
     * Nothing happens when an ad is already up or a timer is already armed, so the
     * repeated PAUSED state changes the video screen reports during a single pause
     * neither stack up timers nor restart the rotation mid-creative.
     */
    onPaused = () => {
        if (!this.shouldDisplayAd || this.adDisplayedStatus || this.showTimerId !== null) {
            return;
        }
        if (!this.getCreative()) {
            Log.info('adlog', 'PauseAds.onPaused() - no creative available', '');
            return;
        }
        this.armShowTimer(this.showAd);
    }

    /**
     * Hold SHOW_DELAY_MS, then run `show`. Every creative goes on screen through
     * here - the first of a pause and each one the rotation brings in - so none of
     * them is ever put up the instant the phase before it ended. Only one can be
     * pending at a time; arming replaces whatever was waiting.
     */
    armShowTimer = (show: () => void) => {
        this.clearShowTimer();
        this.showTimerId = setTimeout(() => {
            this.showTimerId = null;
            show();
        }, SHOW_DELAY_MS);
    }

    /**
     * Playback continued, or something else took the player over (trickplay, an
     * ad break, teardown): drop a pending or on-screen ad. Safe to call when
     * there is nothing to drop, so the video screen can call it on every
     * non-paused state transition.
     */
    onResumed = () => {
        this.clearShowTimer();
        if (this.adDisplayedStatus) {
            this.hideAd();
        }
    }

    /** The app-level cache this instance takes its creatives and its rotation counter from. */
    getCache = () => {
        const { appState } = AppData as AppSetup;
        return appState?.getApp()?.pauseAdsCache || null;
    }

    /**
     * Where in the creative list the next display starts, wrapped into range.
     *
     * The counter is read off the app-level cache rather than held on this
     * instance, because a PauseAds is built for one video and torn down when the
     * viewer leaves the video screen. Keeping it on the app is what makes the
     * rotation continue across videos: creatives 0 and 1 shown on one video means
     * the next video's first pause opens on creative 2, and 0 comes back round
     * only when the list runs out - the same rotation bug ads walk through
     * ads.breakPointAdMapCounter.
     */
    getCreativeIndex = (creativesLen: number): number => {
        const index = this.getCache()?.nextIndex || 0;
        return (creativesLen > 0 && index >= creativesLen) ? 0 : index;
    }

    /** Move the shared counter past the creative just displayed, wrapping at the end of the list. */
    advanceCreativeIndex = (displayedIndex: number, creativesLen: number) => {
        const cache = this.getCache();
        if (!cache || creativesLen <= 0) return;
        cache.nextIndex = (displayedIndex + 1 >= creativesLen) ? 0 : displayedIndex + 1;
    }

    /**
     * The creative to show next, or null when there is nothing displayable. The
     * cache is re-read when empty because the launch prefetch may only have
     * landed after this video started.
     */
    getCreative = () => {
        if (!this.adObj) {
            this.loadCreatives();
        }
        const creatives = this.adObj?.Creatives;
        if (!creatives || creatives.length === 0) {
            return null;
        }
        const creative = creatives[this.getCreativeIndex(creatives.length)];
        return creative?.CreativeData ? creative : null;
    }

    /**
     * Open the banner and keep it there for as long as the pause lasts: the first
     * creative goes up now and each one hands over to the next as it ends, so a
     * long pause keeps getting fresh artwork instead of a single ad that expires
     * and leaves the screen bare. Only the session-level state is set here - the
     * rotation itself touches per-creative state alone.
     */
    showAd = () => {
        const creative = this.getCreative();
        if (!creative?.CreativeData) {
            this.initAdLog(false, false);
            return;
        }
        this.adDisplayedStatus = true;
        this.adCycleCount = 0;
        Logger.processUserAction('PauseAd_Start');
        this.displayCreative(creative);

        if (this.videoPlayer && typeof this.videoPlayer.onPauseAdShown === 'function') {
            this.videoPlayer.onPauseAdShown();
        }
    }

    /**
     * Put one creative on screen and arm the timers that carry it to its end: the
     * midpoint tracker and the hand-off to the next creative. Called once per
     * creative for as long as the pause lasts, so every pass is a display in its
     * own right - its own trackers, its own ad-view log entry - and advances the
     * shared rotation counter so the next one down the list follows it, on this
     * pause or on a pause in some later video.
     *
     * A creative that carries no usable duration gets no end timer and so stays up
     * until the viewer acts, as before.
     */
    displayCreative = (creative: Creative) => {
        const crt = creative.CreativeData!;
        const creatives = this.adObj?.Creatives || [];
        this.currentAdIndex = this.getCreativeIndex(creatives.length);
        this.advanceCreativeIndex(this.currentAdIndex, creatives.length);
        this.adCycleCount++;

        this.currentAdDuration = crt.duration || this.config?.pauseadduration || 0;
        // Drop the previous creative's timers before arming this one's, so a long
        // pause doesn't accumulate spent ids for every creative it rotated through.
        this.clearTrackerTimers();
        this.resetTrackerStatus();
        this.renderAd(crt);
        this.initAdLog(true, true);
        Log.info('adlog', 'PauseAds.displayCreative() - creative ' + this.currentAdIndex + ' shown for '
            + this.currentAdDuration + 's, display no. ', this.adCycleCount);

        this.fireTracker('start');
        if (this.currentAdDuration > 0) {
            this.trackerTimerIds.push(setTimeout(
                () => this.fireTracker('midpoint'),
                Math.round((this.currentAdDuration / 2) * 1000)
            ));
            this.dismissTimerId = setTimeout(() => {
                this.dismissTimerId = null;
                // Ran its full duration, so this one counts as completed.
                this.fireTracker('complete');
                this.rotateAd();
            }, Math.round(this.currentAdDuration * 1000));
        }
    }

    /**
     * The creative on screen has run its full duration while the viewer is still
     * paused: take it off and bring the next one in behind the same appearance
     * delay the first creative waited out, so the rotation is a series of separate
     * displays rather than artwork cutting straight to other artwork. getCreative()
     * wraps at the end of the list, so the rotation lasts as long as the pause does
     * - with a single creative that means the same artwork re-displaying - and only
     * a key press, a resume or teardown stops it.
     *
     * The pause-ad session stays open across the gap (adDisplayedStatus stays true,
     * the video screen is not told the ad went away), so the ad keeps owning input
     * and a key pressed between two creatives dismisses the rotation as it would
     * dismiss a visible ad - hideAd() cancels the pending one.
     */
    rotateAd = () => {
        if (!this.adDisplayedStatus) return;
        const next = this.getCreative();
        if (!next?.CreativeData) {
            Log.info('adlog', 'PauseAds.rotateAd() - no creative left to rotate to', '');
            this.hideAd();
            return;
        }
        this.removeContainer();
        this.armShowTimer(() => this.displayCreative(next));
    }

    /**
     * Build the overlay and put it flush against the right edge, spanning the full
     * height of the screen.
     *
     * The height is taken from the panel rather than scaled off the creative, so
     * the banner reaches both screen edges vertically whatever resolution the
     * creative was calibrated for, and there is no inset on the right. Width then
     * follows from the creative's own aspect ratio so the artwork is never
     * stretched to get there. The ad server's xpos/ypos are ignored - the placement
     * is fixed by the format.
     */
    renderAd = (crt: CreativeData) => {
        // Drop whatever is there first so only one node ever holds the id: the
        // creative this one rotates in after, or a banner still fading out from a
        // pause the viewer resumed and immediately re-entered.
        this.removeContainer(true);

        const height = window.innerHeight;
        const width = this.bannerWidth(crt, height);
        const size = (width ? 'width: ' + width + 'px;' : '') + 'height: ' + height + 'px;';

        const adHtml = '<div id="' + CONTAINER_ID + '" style="position: absolute;'
            + 'top: 0px; right: 0px; left: auto;'
            + size
            + 'z-index: ' + PAUSE_AD_Z_INDEX + ';'
            + 'cursor: pointer; display: none;'
            + '"><img style="' + size + 'display: block;" src="' + crt.url + '"></div>';

        $('body').append(adHtml);
        $('#' + CONTAINER_ID).fadeIn(FADE_MS);
        $('#' + CONTAINER_ID).on('click', () => {
            Logger.processUserAction('PauseAd_Click');
            this.activate();
        });
    }

    /**
     * The width a full-screen-height banner needs to keep the creative's aspect
     * ratio, capped at the screen width. When the creative JSON gives only a width
     * there is no ratio to work from, so that width is scaled off its reference
     * resolution instead; when it gives neither, 0 is returned and the height
     * alone drives the image, which sizes itself proportionally.
     */
    bannerWidth = (crt: CreativeData, height: number): number => {
        if (crt.width && crt.height) {
            return Math.min(window.innerWidth, Math.round(crt.width * (height / crt.height)));
        }
        if (crt.width) {
            const scaleX = window.innerWidth / (crt.refWidth || DESIGN_FRAME.width);
            return Math.min(window.innerWidth, Math.round(crt.width * scaleX));
        }
        return 0;
    }

    /**
     * Take the ad off screen and leave the video exactly as it was - still paused.
     * Used by anything that supersedes the ad; the close-and-continue path is
     * dismissAndResume().
     *
     * A creative still waiting out an appearance delay is cancelled too - the one a
     * pause is about to show, or the next one in a rotation gap. Callers that
     * supersede the ad (the video screen's pass-through keys, trickplay) mean
     * "nothing should be on screen", and a pending timer would otherwise fire behind
     * them; before the first creative is up adDisplayedStatus is still false, so the
     * early return below cannot be relied on to cover it.
     */
    hideAd = () => {
        this.clearShowTimer();
        if (!this.adDisplayedStatus) return;
        Log.info('adlog', 'PauseAds.hideAd() - removing pause ad after displays: ', this.adCycleCount);
        this.clearDismissTimer();
        this.clearTrackerTimers();
        this.adDisplayedStatus = false;
        this.removeContainer();
        // One End per pause, carrying how many creatives the rotation got through;
        // each of those displays logged its own ad view from displayCreative().
        Logger.processUserAction('PauseAd_End', String(this.adCycleCount));
        this.adCycleCount = 0;
        if (this.videoPlayer && typeof this.videoPlayer.onPauseAdHidden === 'function') {
            this.videoPlayer.onPauseAdHidden();
        }
    }

    /**
     * OK/click on the ad: run whatever action it carries, then close it and
     * continue playback - the same thing every other key does.
     */
    activate = () => {
        this.executeActions();
        this.dismissAndResume();
    }

    /**
     * Close the ad and hand playback back to the video screen. Every key press
     * while the ad is up lands here, as does a click on the creative.
     */
    dismissAndResume = () => {
        this.hideAd();
        if (this.videoPlayer && typeof this.videoPlayer.resumeFromPauseAd === 'function') {
            this.videoPlayer.resumeFromPauseAd();
        }
    }

    /**
     * Run the action the ad server attached to the creative. Pause-ad creatives
     * name their action in the value rather than the key
     * (`{"enter": "paidSubscription"}`), so it is resolved by value.
     *
     * paidSubscription is the only action served today and this app has no
     * subscription flow, so it is logged and the ad simply closes. This switch is
     * the single place to route to that screen once it exists.
     */
    executeActions = () => {
        const creative = this.adObj?.Creatives?.[this.currentAdIndex];
        const action = VastParser.getPauseActionName(creative?.actions);
        Log.info('adlog', 'PauseAds.executeActions() - action: ', action);
        if (!action) return;

        switch (action) {
            case 'paidSubscription':
                Logger.processUserAction('PauseAd_Action_paidSubscription');
                // TODO: route to the paid-subscription screen once the app has one.
                break;
            default:
                Logger.processUserAction('PauseAd_Action_' + action);
                break;
        }
    }

    /**
     * Ping the tracker for `event` once. A pause ad is timed off the wall clock
     * rather than the video clock, so the quartiles are driven by the timers set
     * in showAd() instead of a time-update poll - which also means an ad closed
     * early never reports a midpoint or complete it did not reach.
     */
    fireTracker = (event: 'start' | 'midpoint' | 'complete') => {
        if (this.trackersStatus[event]) return;
        this.trackersStatus[event] = true;
        const creative = this.adObj?.Creatives?.[this.currentAdIndex];
        if (event === 'start') {
            if (this.adObj?.Impression) {
                this.pingTracker(this.adObj.Impression);
            }
            if (creative?.Impression) {
                this.pingTracker(creative.Impression);
            }
        }
        const url = creative?.trackers?.[event];
        Log.info('adlog', 'PauseAds.fireTracker() - ' + event + ' tracker', url || 'none');
        if (url) {
            this.pingTracker(url);
        }
    }

    pingTracker = (url: string): void => {
        fetch(url).catch(() => {
            Log.info('adlog', 'PauseAds.pingTracker() - failed', url);
        });
    }

    resetTrackerStatus = () => {
        this.trackersStatus.start = false;
        this.trackersStatus.midpoint = false;
        this.trackersStatus.complete = false;
    }

    clearShowTimer = () => {
        if (this.showTimerId !== null) {
            Log.info('adlog', 'PauseAds.clearShowTimer() - pending pause ad cancelled', '');
            clearTimeout(this.showTimerId);
            this.showTimerId = null;
        }
    }

    clearDismissTimer = () => {
        if (this.dismissTimerId !== null) {
            clearTimeout(this.dismissTimerId);
            this.dismissTimerId = null;
        }
    }

    clearTrackerTimers = () => {
        this.trackerTimerIds.forEach(id => clearTimeout(id));
        this.trackerTimerIds = [];
    }

    /**
     * Drop the overlay node. Teardown removes it outright rather than fading:
     * a fade would leave the node alive for FADE_MS after the page is gone, long
     * enough for the next video to append a second node with the same id.
     */
    removeContainer = (immediate = false) => {
        const el = $('#' + CONTAINER_ID);
        if (!el.length) return;
        el.off('click');
        if (immediate) {
            el.remove();
        } else {
            el.fadeOut(FADE_MS, function () {
                $(this).remove();
            });
        }
    }

    initAdLog = (adAvailable: boolean, adPlayed: boolean) => {
        var adDetails: any = {};
        adDetails.adProvider = 'futuretodayinc';
        adDetails.adPriority = this.config?.adList?.priority || 0;
        adDetails.adAvailable = adAvailable;
        adDetails.adPlayed = adPlayed;
        adDetails.adType = 'pausead';
        adDetails.transactionId = Helper.guid(null);

        if (adAvailable == true && this.adObj) {
            adDetails.adTitle = this.adObj.AdTitle == undefined ? 'Pause Ad' : this.adObj.AdTitle;
            adDetails.adId = this.adObj.adId == undefined ? 1 : this.adObj.adId;
        }

        Logger.logAdView(adDetails);
    }

    deInit = () => {
        Log.info('adlog', 'PauseAds.deInit() - called', '');
        this.shouldDisplayAd = false;
        this.adDisplayedStatus = false;
        this.adCycleCount = 0;
        this.adObj = null;
        this.clearShowTimer();
        this.clearDismissTimer();
        this.clearTrackerTimers();
        this.resetTrackerStatus();
        this.removeContainer(true);
        $('head link[data-pausead="true"]').remove();
    }
}
